// BattleDetail.jsx (FULLY FIXED - Duplicate tick & last-box-replay bug resolved)
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import "./battleDetail.css";
import BattleRoulete from "./BattleRoulete";
import BattleHeader from "./BattleHeader";
import { useParams } from "react-router-dom";
import {
  fetchBattleDetails,
  fetchBattleResult,
  enterBattle,
} from "../../api/modules/b2droppie.class";
import FullAppLoader from "../Loader/FullAppLoader";
import { useDispatch, useSelector } from "react-redux";
import { toggleLoader } from "../../redux/slices/loaderSlice";
import BattleFooter from "./BattleFooter";
import BattleEnd from "./BattleEnd";
import BattleWait from "./BattleWait";
import CoinFlip from "./CoinFlip";
import Countdown from "../countDown/CountDown";
import PageNotFound from "../BattleNotFound/BattleNotFound";
import { battleWSManager } from "../../services/battleWebSocketManager";
import {
  setBattle,
  clearBattle,
  updateBattle,
  updatePlayer1,
  updatePlayer2,
  setPlayer2Joined,
  removeFirstEvent,
  setProcessing,
  clearEventQueue,
} from "../../redux/slices/battleSlice";
import rollSound from "../../assets/sound/reel-1.mp3";
import winSound from "../../assets/sound/win2.mp3";

const BattleDetail = () => {
  const { slugId } = useParams();
  const dispatch = useDispatch();

  // Redux state
  const battle = useSelector((state) => state.battle.currentBattle);
  const eventQueue = useSelector((state) => state.battle.eventQueue);
  const player2Joined = useSelector((state) => state.battle.ui.player2Joined);
  const isLoading = useSelector((state) => state.loader.isLoading);
  const user = useSelector((state) => state.auth.user);

  // Local UI state
  const [shareLink, setShareLink] = useState("");
  const [showOtherComponent, setShowOtherComponent] = useState(false);
  const [flipWinner, setFlipWinner] = useState(null);
  const [battleStarted, setBattleStarted] = useState(false);
  const [showStartCountdown, setShowStartCountdown] = useState(false);
  const [isWaitingOverride, setIsWaitingOverride] = useState(false);
  const [isCoinFlipping, setIsCoinFlipping] = useState(false);
  const [coinAnimationComplete, setCoinAnimationComplete] = useState(false);
  const [isReplaying, setIsReplaying] = useState(false);
  const [isLateJoin, setIsLateJoin] = useState(false);
  const [hasEnteredBattle, setHasEnteredBattle] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Refs
  const processingRef = useRef(false);
  const countdownShownRef = useRef(false);
  const startedEventReceivedRef = useRef(false);
  const prevBattleUuidRef = useRef(null);
  const initializedSlugRef = useRef(null);
  const isLateJoinRef = useRef(false);

  // ✅ FIX: Track the last processed box_index to prevent double-processing
  const lastProcessedBoxIndexRef = useRef(-1);

  // Replay cancellation refs
  const replayCancelledRef = useRef(false);
  const replayFinishedStateRef = useRef(null);

  // Decrypt battle ID
  const decryptedBattleId = useMemo(() => {
    try {
      const decodedData = parseInt(window.atob(slugId));
      return decodedData;
    } catch (error) {
      console.error("Decryption error:", error);
      return null;
    }
  }, [slugId]);

  const decryptData = useCallback(() => decryptedBattleId, [decryptedBattleId]);

  // Helper: Delay function
  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Cancellable delay for replays
  const replayDelay = (ms) =>
    new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        if (replayCancelledRef.current) {
          reject(new Error("REPLAY_CANCELLED"));
        } else {
          resolve();
        }
      }, ms);
      const checkInterval = setInterval(() => {
        if (replayCancelledRef.current) {
          clearTimeout(timer);
          clearInterval(checkInterval);
          reject(new Error("REPLAY_CANCELLED"));
        }
      }, 200);
      const origResolve = resolve;
      resolve = () => {
        clearInterval(checkInterval);
        origResolve();
      };
    });

  const formatWonProductsFromAPI = useCallback(
    (wonProducts, playerId, creatorId, currentTick) => {
      if (!wonProducts || !Array.isArray(wonProducts)) return [];

      const playerProducts = wonProducts.filter(
        (item) => item.player_id === playerId
      );
      const currentRunningBoxIndex = currentTick - 1;

      const filteredProducts = playerProducts.filter(
        (item) => item.box_index < currentRunningBoxIndex
      );

      return filteredProducts.map((item) => ({
        image: item.product_image,
        name: item.product_name,
        price: parseFloat(item.product_value || 0),
      }));
    },
    []
  );

  // Helper: Find latest tick in queue
  const skipToLatestTick = useCallback(
    (queue) => {
      const tickEvents = queue.filter((event) => {
        const eventChannel = event._channel;
        const expectedChannel = `battle.${battle?.uuid}`;
        return (
          event.type === "tick" &&
          (!eventChannel || eventChannel === expectedChannel)
        );
      });

      if (tickEvents.length === 0) return null;

      const latestTick = tickEvents.reduce((latest, current) => {
        return current.box_index > latest.box_index ? current : latest;
      }, tickEvents[0]);

      return latestTick;
    },
    [battle?.uuid]
  );

  // CRITICAL: Reset ALL state when slugId changes
  useEffect(() => {
    if (initializedSlugRef.current && initializedSlugRef.current !== slugId) {
      setShareLink("");
      setShowOtherComponent(false);
      setFlipWinner(null);
      setBattleStarted(false);
      setShowStartCountdown(false);
      setIsWaitingOverride(false);
      setIsCoinFlipping(false);
      setCoinAnimationComplete(false);
      setIsReplaying(false);
      setIsLateJoin(false);
      isLateJoinRef.current = false;
      setHasEnteredBattle(false);
      setIsInitialized(false);

      processingRef.current = false;
      countdownShownRef.current = false;
      startedEventReceivedRef.current = false;
      replayCancelledRef.current = false;
      replayFinishedStateRef.current = null;
      // ✅ FIX: Reset last processed box index
      lastProcessedBoxIndexRef.current = -1;

      dispatch(clearBattle());
      dispatch(clearEventQueue());

      if (prevBattleUuidRef.current) {
        battleWSManager.forceUnsubscribe(prevBattleUuidRef.current);
        prevBattleUuidRef.current = null;
      }
    }

    initializedSlugRef.current = slugId;
  }, [slugId, dispatch]);

  // Check countdown state
  const countDown = useMemo(() => {
    if (!decryptedBattleId) return false;
    return JSON.parse(
      sessionStorage.getItem(`countDown-${decryptedBattleId}`) || "false"
    );
  }, [decryptedBattleId]);

  const handleCountdownComplete = useCallback(() => {
    setShowOtherComponent(true);
    if (decryptedBattleId) {
      sessionStorage.setItem(
        `countDown-${decryptedBattleId}`,
        JSON.stringify(false)
      );
    }
  }, [decryptedBattleId]);

  const handleStartCountdownComplete = useCallback(() => {
    setBattleStarted(true);
    setShowStartCountdown(false);
    countdownShownRef.current = true;

    if (battle?.id && decryptedBattleId) {
      sessionStorage.setItem(`battleStarted-${battle.id}`, "true");
      sessionStorage.setItem(`countdownShown-${decryptedBattleId}`, "true");
      sessionStorage.removeItem(`showStartCountdown-${decryptedBattleId}`);
    }

    dispatch(
      updateBattle({
        status: "running",
      })
    );
  }, [battle?.id, decryptedBattleId, dispatch]);

const handleSecondPlayerJoined = useCallback(
  async (data) => {
    dispatch(setPlayer2Joined(true));
    const joinedPlayer = data.player;

    if (joinedPlayer) {
      dispatch((dispatch, getState) => {
        const existingUser2 = getState().battle.currentBattle?.user_2;

        dispatch(
          updateBattle({
            user_2: {
              id: joinedPlayer.user_id,        // ✅ .id → .user_id
              user_id: joinedPlayer.user_id,   // ✅ .id → .user_id
              user: {
                id: joinedPlayer.user_id,      // ✅ .id → .user_id
                username: joinedPlayer.username,
              },
              userdetails: {
                id: joinedPlayer.user_id,      // ✅ .id → .user_id
                username: joinedPlayer.username,
                avatar: joinedPlayer?.avatar,
              },
              winner: false,
              total_products: existingUser2?.total_products || [],
              total_unboxed_value: existingUser2?.total_unboxed_value || 0,
              total_amount: existingUser2?.total_amount || 0,
            },
          })
        );
      });
    }

    await delay(500);
  },
  [dispatch]
);

  const handleBattleStarted = useCallback(
    async (data) => {
      if (!isInitialized) {
        return;
      }

      if (isLateJoinRef.current) {
        countdownShownRef.current = true;
        startedEventReceivedRef.current = true;
        setBattleStarted(true);
        setShowStartCountdown(false);
        dispatch(
          updateBattle({
            status: "running",
            started_at: data?.started_at || new Date().toISOString(),
          })
        );
        return;
      }

      if (battleStarted) {
        countdownShownRef.current = true;
        startedEventReceivedRef.current = true;
        dispatch(
          updateBattle({
            status: "running",
            started_at: data?.started_at || new Date().toISOString(),
          })
        );
        return;
      }

      countdownShownRef.current = false;

      if (decryptedBattleId) {
        localStorage.removeItem(`battle_waiting_${decryptedBattleId}`);
      }

      startedEventReceivedRef.current = true;

      const countdownAlreadyShown = decryptedBattleId
        ? sessionStorage.getItem(`countdownShown-${decryptedBattleId}`)
        : null;

      if (countdownAlreadyShown === "true") {
        setBattleStarted(true);
        setShowStartCountdown(false);
        dispatch(
          updateBattle({
            status: "running",
            started_at: data?.started_at || new Date().toISOString(),
          })
        );
        return;
      }

      setBattleStarted(false);
      setShowStartCountdown(true);
      setIsWaitingOverride(false);

      await new Promise((resolve) => setTimeout(resolve, 50));

      if (decryptedBattleId) {
        sessionStorage.setItem(
          `showStartCountdown-${decryptedBattleId}`,
          JSON.stringify(true)
        );
      }

      dispatch(
        updateBattle({
          status: "starting",
          started_at: data?.started_at || new Date().toISOString(),
        })
      );

      await new Promise((resolve) => {
        let intervalCleared = false;

        const checkInterval = setInterval(() => {
          if (countdownShownRef.current === true) {
            if (!intervalCleared) {
              clearInterval(checkInterval);
              intervalCleared = true;
            }
            resolve();
          }
        }, 100);

        setTimeout(() => {
          if (!intervalCleared) {
            clearInterval(checkInterval);
            intervalCleared = true;
          }
          resolve();
        }, 6000);
      });
    },
    [isInitialized, battleStarted, decryptedBattleId, dispatch]
  );

  const handleTickEvent = useCallback(
  async (data) => {
    if (!isInitialized) {
      return;
    }

    // Guard against processing the same box_index twice.
    if (data.box_index <= lastProcessedBoxIndexRef.current) {
      console.warn(
        `⚠️ Skipping duplicate tick for box_index ${data.box_index} (last processed: ${lastProcessedBoxIndexRef.current})`
      );
      return;
    }
    lastProcessedBoxIndexRef.current = data.box_index;

    if (isLateJoinRef.current && !battleStarted) {
      setBattleStarted(true);
      setShowStartCountdown(false);
      setIsWaitingOverride(false);
      countdownShownRef.current = true;
      startedEventReceivedRef.current = true;
    }

    const countdownAlreadyShown = decryptedBattleId
      ? sessionStorage.getItem(`countdownShown-${decryptedBattleId}`)
      : null;
    const isCountdownComplete =
      countdownAlreadyShown === "true" || countdownShownRef.current === true;

    if (
      (showStartCountdown || battle?.status === "starting") &&
      !isCountdownComplete
    ) {
      await new Promise((resolve) => {
        let intervalCleared = false;

        const checkInterval = setInterval(() => {
          const isComplete =
            countdownShownRef.current === true ||
            (decryptedBattleId &&
              sessionStorage.getItem(
                `countdownShown-${decryptedBattleId}`
              ) === "true");
          if (isComplete) {
            if (!intervalCleared) {
              clearInterval(checkInterval);
              intervalCleared = true;
            }
            resolve();
          }
        }, 100);

        setTimeout(() => {
          if (!intervalCleared) {
            clearInterval(checkInterval);
            intervalCleared = true;
          }
          countdownShownRef.current = true;
          setBattleStarted(true);
          setShowStartCountdown(false);
          resolve();
        }, 7000);
      });
    }

    const startedEventReceived = startedEventReceivedRef.current === true;

    if (
      data.box_index === 0 &&
      !startedEventReceived &&
      !isCountdownComplete &&
      !isLateJoinRef.current
    ) {
      await new Promise((resolve) => {
        let intervalCleared = false;

        const checkInterval = setInterval(() => {
          if (startedEventReceivedRef.current === true) {
            if (!intervalCleared) {
              clearInterval(checkInterval);
              intervalCleared = true;
            }
            resolve();
          }
        }, 100);

        setTimeout(() => {
          if (!intervalCleared) {
            clearInterval(checkInterval);
            intervalCleared = true;
          }
          resolve();
        }, 2000);
      });
    }

    if (
      !battleStarted &&
      !showStartCountdown &&
      !startedEventReceived &&
      !isCountdownComplete &&
      !isLateJoinRef.current &&
      data.box_index > 0
    ) {
      setIsLateJoin(true);
      isLateJoinRef.current = true;
      setBattleStarted(true);
      setShowStartCountdown(false);
      setIsWaitingOverride(false);

      if (battle?.id && decryptedBattleId) {
        sessionStorage.setItem(`battleStarted-${battle.id}`, "true");
        sessionStorage.setItem(`countdownShown-${decryptedBattleId}`, "true");
      }

      countdownShownRef.current = true;

      if (battle?.status !== "running") {
        dispatch(
          updateBattle({
            status: "running",
            started_at: new Date().toISOString(),
          })
        );
      }
    }

    const transformedProducts =
      data.selectedProducts?.map((product) => ({
        id: product.product_id,
        name: product.product_name,
        image: product.product_image,
        value: product.product_value,
        user_id: product.user_id,
        player_id: product.player_id,
      })) || [];

    const player1Product = data.selectedProducts?.find(
      (p) => p.user_id === battle?.creator_id
    );
    const player2Product = data.selectedProducts?.find(
      (p) => p.user_id !== battle?.creator_id
    );

    const updatedPlayers =
      data.players?.map((player) => ({
        id: player.player_id,
        user_id: player.user_id,
        user: {
          id: player.user_id,
          username: player.username,
        },
        total_unboxed_value: player.total_unboxed_value,
      })) || [];

    const currentBox = battle?.boxes?.[data.box_index];
    const boxProducts =
      currentBox?.box?.products?.map((p) => ({
        id: p.id,
        name: p.name,
        image: p.image,
        value: parseFloat(p.price || p.value || 0),
        min_limit: p.pivot?.min_limit || p.min_limit,
        max_limit: p.pivot?.max_limit || p.max_limit,
      })) || [];

    // Dispatch box products so the roulette can start animating
    dispatch(
      updateBattle({
        status: "running",
        players: updatedPlayers,
        box_products: transformedProducts,
        all_box_products: boxProducts,
        box_detail: {
          ...battle?.box_detail,
          current_box: data.box_index + 1,
        },
      })
    );

    dispatch(
      updatePlayer1({
        user_product: player1Product
          ? {
              id: player1Product.product_id,
              name: player1Product.product_name,
              image: player1Product.product_image,
              value: player1Product.product_value,
            }
          : undefined,
      })
    );

    dispatch(
      updatePlayer2({
        user_product: player2Product
          ? {
              id: player2Product.product_id,
              name: player2Product.product_name,
              image: player2Product.product_image,
              value: player2Product.product_value,
            }
          : undefined,
      })
    );

    // ── Fix 3: Wait for the roulette animation to finish (~5 s) BEFORE
    //    updating the running total and won-products list.
    //    Previously this all happened at the 10 s mark (next-box time),
    //    which made the displayed value jump when the next box started.
    await delay(5200); // ← reel animation is 5 s; 200 ms buffer

    // Update running totals exactly when the winning product has stopped
    dispatch((dispatch, getState) => {
      const currentState = getState().battle.currentBattle;
      const player1WonProducts = [
        ...(currentState?.user_1?.total_products || []),
      ];
      const player2WonProducts = [
        ...(currentState?.user_2?.total_products || []),
      ];

      let player1NewTotal = currentState?.user_1?.total_unboxed_value || 0;
      let player2NewTotal = currentState?.user_2?.total_unboxed_value || 0;

      if (player1Product) {
        player1WonProducts.push({
          image: player1Product.product_image,
          name: player1Product.product_name,
          price: player1Product.product_value,
        });
        player1NewTotal += parseFloat(player1Product.product_value);
      }

      if (player2Product) {
        player2WonProducts.push({
          image: player2Product.product_image,
          name: player2Product.product_name,
          price: player2Product.product_value,
        });
        player2NewTotal += parseFloat(player2Product.product_value);
      }

      dispatch(
        updatePlayer1({
          total_products: player1WonProducts,
          total_unboxed_value: player1NewTotal,
        })
      );
      dispatch(
        updatePlayer2({
          total_products: player2WonProducts,
          total_unboxed_value: player2NewTotal,
        })
      );
    });

    // ── Wait the remaining time before the queue releases the next event ──────
    await delay(4800);
  },
  [
    showStartCountdown,
    battleStarted,
    isInitialized,
    decryptedBattleId,
    battle?.status,
    battle?.creator_id,
    battle?.boxes,
    battle?.box_detail,
    battle?.id,
    dispatch,
  ]
);

  const handleFlipEvent = useCallback(
    async (data) => {
      if (!isInitialized) {
        return;
      }

      if (!battleStarted && !startedEventReceivedRef.current) {
        setBattleStarted(true);
        setShowStartCountdown(false);
        setIsLateJoin(true);
        isLateJoinRef.current = true;
        setIsWaitingOverride(false);

        if (battle?.id && decryptedBattleId) {
          sessionStorage.setItem(
            `battleStarted-${battle.id}`,
            "true"
          );
          sessionStorage.setItem(
            `countdownShown-${decryptedBattleId}`,
            "true"
          );
        }

        countdownShownRef.current = true;
      }

      const player1Data = data.players?.find(
        (p) => p.user_id === battle?.creator_id
      );
      const player2Data = data.players?.find(
        (p) => p.user_id !== battle?.creator_id
      );

      const winnerId = player1Data?.is_winner
        ? player1Data.user_id
        : player2Data?.is_winner
        ? player2Data.user_id
        : null;

      if (winnerId) {
        setFlipWinner(winnerId);
      }

      dispatch(
        updateBattle({
          flip: true,
          status: "flipping",
          flipData: data,
        })
      );

      dispatch(
        updatePlayer1({
          total_amount: player1Data?.total_unboxed_value || 0,
          total_unboxed_value: player1Data?.total_unboxed_value || 0,
        })
      );

      dispatch(
        updatePlayer2({
          total_amount: player2Data?.total_unboxed_value || 0,
          total_unboxed_value: player2Data?.total_unboxed_value || 0,
        })
      );

      setIsCoinFlipping(true);
      setCoinAnimationComplete(false);
      await delay(6000);
    },
    [
      battleStarted,
      isInitialized,
      battle?.creator_id,
      battle?.id,
      battle?.user_1,
      battle?.user_2,
      decryptedBattleId,
      dispatch,
    ]
  );

  const handleBattleEnded = useCallback(
    async (data) => {
      if (!isInitialized) {
        return;
      }

      if (!battleStarted && !startedEventReceivedRef.current) {
        setBattleStarted(true);
        setShowStartCountdown(false);
        setIsLateJoin(true);
        isLateJoinRef.current = true;
        setIsWaitingOverride(false);

        if (battle?.id && decryptedBattleId) {
          sessionStorage.setItem(
            `battleStarted-${battle.id}`,
            "true"
          );
          sessionStorage.setItem(
            `countdownShown-${decryptedBattleId}`,
            "true"
          );
        }

        countdownShownRef.current = true;
      }

      let apiResponse;
      try {
        apiResponse = await fetchBattleResult(
          JSON.stringify(decryptedBattleId)
        );

        if (!apiResponse || !apiResponse.players) {
          console.error("❌ Failed to fetch battle results");
          apiResponse = null;
        }
      } catch (error) {
        console.error("❌ Error fetching battle results:", error);
        apiResponse = null;
      }

      let player1Data, player2Data, player1Products, player2Products;

      if (apiResponse) {
        player1Data = apiResponse.players.find(
          (p) => p.user_id === battle?.creator_id
        );
        player2Data = apiResponse.players.find(
          (p) => p.user_id !== battle?.creator_id
        );

        player1Products =
          apiResponse.wonProducts?.filter(
            (p) => p.player_id === player1Data?.player_id
          ) || [];
        player2Products =
          apiResponse.wonProducts?.filter(
            (p) => p.player_id === player2Data?.player_id
          ) || [];
      } else {
        player1Data = data.players?.find(
          (p) => p.user_id === battle?.creator_id
        );
        player2Data = data.players?.find(
          (p) => p.user_id !== battle?.creator_id
        );

        player1Products =
          data.wonProducts?.filter(
            (p) => p.player_id === player1Data?.player_id
          ) || [];
        player2Products =
          data.wonProducts?.filter(
            (p) => p.player_id === player2Data?.player_id
          ) || [];
      }

      const formatProducts = (products) =>
        products.map((item) => ({
          image: item.product_image,
          name: item.product_name,
          price: item.product_value,
        }));

      const winnerId = player1Data?.is_winner
        ? player1Data.user_id
        : player2Data?.is_winner
        ? player2Data.user_id
        : null;

      if (winnerId) {
        setFlipWinner(winnerId);
      }

      dispatch(
        updateBattle({
          status: "finished",
          finished_at: data.ended_at || new Date().toISOString(),
        })
      );

      dispatch(
        updatePlayer1({
          winner: player1Data?.is_winner || false,
          total_unboxed_value: player1Data?.total_unboxed_value || 0,
          total_amount: player1Data?.total_unboxed_value || 0,
          total_products: formatProducts(player1Products),
        })
      );

      dispatch(
        updatePlayer2({
          winner: player2Data?.is_winner || false,
          total_unboxed_value: player2Data?.total_unboxed_value || 0,
          total_amount: player2Data?.total_unboxed_value || 0,
          total_products: formatProducts(player2Products),
        })
      );

      if (isCoinFlipping) {
        await delay(5000);
      }

      dispatch(updateBattle({ flip: false }));
      setIsCoinFlipping(false);
    },
    [
      battleStarted,
      isInitialized,
      isCoinFlipping,
      battle?.creator_id,
      battle?.id,
      decryptedBattleId,
      dispatch,
    ]
  );

  const calculateRunningTotal = (iterations, userId, upToBoxIndex) => {
    let total = 0;
    iterations.forEach((iteration) => {
      if (iteration.box_index <= upToBoxIndex) {
        const userProduct = iteration.selectedProducts.find(
          (p) => p.user_id === userId
        );
        if (userProduct) {
          total += parseFloat(userProduct.product_value);
        }
      }
    });
    return total;
  };

 const handleReplayTick = async (groupedIteration, boxIndex, apiResponse) => {
  if (replayCancelledRef.current) {
    throw new Error("REPLAY_CANCELLED");
  }

  const selectedProducts = groupedIteration.selectedProducts;

  const player1Product = selectedProducts.find(
    (p) => p.user_id === apiResponse.players[0].user_id
  );
  const player2Product = selectedProducts.find(
    (p) => p.user_id === apiResponse.players[1].user_id
  );

  const previousBoxIndex = boxIndex - 1;
  const player1Total =
    previousBoxIndex >= 0
      ? calculateRunningTotal(
          apiResponse.iterations,
          apiResponse.players[0].user_id,
          previousBoxIndex
        )
      : 0;
  const player2Total =
    previousBoxIndex >= 0
      ? calculateRunningTotal(
          apiResponse.iterations,
          apiResponse.players[1].user_id,
          previousBoxIndex
        )
      : 0;

  const currentBox = battle?.boxes?.[boxIndex];
  const allBoxProducts =
    currentBox?.box?.products?.map((p) => ({
      id: p.id,
      name: p.name,
      image: p.image,
      value: parseFloat(p.value || p.price || 0),
      min_limit: p.min_limit,
      max_limit: p.max_limit,
    })) || [];

  const transformedProducts = selectedProducts.map((p) => ({
    id: p.product_id,
    name: p.product_name,
    image: p.product_image,
    value: p.product_value,
    user_id: p.user_id,
    player_id: p.player_id,
  }));

  if (replayCancelledRef.current) {
    throw new Error("REPLAY_CANCELLED");
  }

  // Dispatch products so the roulette starts animating
  dispatch(
    updateBattle({
      box_products: transformedProducts,
      all_box_products: allBoxProducts,
      box_detail: {
        ...battle?.box_detail,
        current_box: boxIndex,
        animating_box: boxIndex + 1,
      },
    })
  );

  dispatch(
    updatePlayer1({
      total_unboxed_value: player1Total,
      user_product: player1Product
        ? {
            id: player1Product.product_id,
            name: player1Product.product_name,
            image: player1Product.product_image,
            value: player1Product.product_value,
          }
        : undefined,
    })
  );

  dispatch(
    updatePlayer2({
      total_unboxed_value: player2Total,
      user_product: player2Product
        ? {
            id: player2Product.product_id,
            name: player2Product.product_name,
            image: player2Product.product_image,
            value: player2Product.product_value,
          }
        : undefined,
    })
  );

  // ── Fix 3: Wait for reel animation to complete before updating totals ───────
  await replayDelay(5200);

  if (replayCancelledRef.current) {
    throw new Error("REPLAY_CANCELLED");
  }

  // Calculate totals including this box — dispatched when the reel stops
  const player1TotalAfter = calculateRunningTotal(
    apiResponse.iterations,
    apiResponse.players[0].user_id,
    boxIndex
  );
  const player2TotalAfter = calculateRunningTotal(
    apiResponse.iterations,
    apiResponse.players[1].user_id,
    boxIndex
  );

  dispatch((dispatch, getState) => {
    const currentState = getState().battle.currentBattle;
    const player1WonProducts = [
      ...(currentState?.user_1?.total_products || []),
    ];
    const player2WonProducts = [
      ...(currentState?.user_2?.total_products || []),
    ];

    if (player1Product) {
      player1WonProducts.push({
        image: player1Product.product_image,
        name: player1Product.product_name,
        price: player1Product.product_value,
      });
    }

    if (player2Product) {
      player2WonProducts.push({
        image: player2Product.product_image,
        name: player2Product.product_name,
        price: player2Product.product_value,
      });
    }

    dispatch(
      updateBattle({
        box_detail: {
          ...currentState?.box_detail,
          current_box: boxIndex + 1,
          animating_box: boxIndex + 1,
        },
      })
    );

    dispatch(
      updatePlayer1({
        total_products: player1WonProducts,
        total_unboxed_value: player1TotalAfter,
      })
    );
    dispatch(
      updatePlayer2({
        total_products: player2WonProducts,
        total_unboxed_value: player2TotalAfter,
      })
    );
  });

  // ── Wait the remaining buffer before the replay moves to the next box ───────
  await replayDelay(4800);
};

  const handleReplayFlip = async (apiResponse) => {
    if (replayCancelledRef.current) {
      throw new Error("REPLAY_CANCELLED");
    }

    const winnerId = apiResponse.players[0].is_winner
      ? apiResponse.players[0].user_id
      : apiResponse.players[1].is_winner
      ? apiResponse.players[1].user_id
      : null;

    if (winnerId) {
      setFlipWinner(winnerId);
    }

    dispatch(
      updateBattle({
        flip: true,
        status: "flipping",
      })
    );

    dispatch(
      updatePlayer1({
        total_amount: parseFloat(
          apiResponse.players[0].total_unboxed_value
        ),
        total_unboxed_value: parseFloat(
          apiResponse.players[0].total_unboxed_value
        ),
      })
    );

    dispatch(
      updatePlayer2({
        total_amount: parseFloat(
          apiResponse.players[1].total_unboxed_value
        ),
        total_unboxed_value: parseFloat(
          apiResponse.players[1].total_unboxed_value
        ),
      })
    );

    setIsCoinFlipping(true);
    setCoinAnimationComplete(false);
    await replayDelay(6000);
  };

  const handleReplayEnd = async (apiResponse) => {
    if (replayCancelledRef.current) {
      throw new Error("REPLAY_CANCELLED");
    }

    const player1Data = apiResponse.players[0];
    const player2Data = apiResponse.players[1];

    const player1Products =
      apiResponse.wonProducts?.filter(
        (p) => p.player_id === player1Data.player_id
      ) || [];

    const player2Products =
      apiResponse.wonProducts?.filter(
        (p) => p.player_id === player2Data.player_id
      ) || [];

    const formatProducts = (products) =>
      products.map((item) => ({
        image: item.product_image,
        name: item.product_name,
        price: item.product_value,
      }));

    const winnerId = player1Data.is_winner
      ? player1Data.user_id
      : player2Data.is_winner
      ? player2Data.user_id
      : null;

    if (winnerId) {
      setFlipWinner(winnerId);
    }

    dispatch(
      updateBattle({
        status: "finished",
        finished_at: apiResponse.battle.finished_at,
      })
    );

    dispatch(
      updatePlayer1({
        winner: Boolean(player1Data.is_winner),
        total_unboxed_value: parseFloat(player1Data.total_unboxed_value),
        total_amount: parseFloat(player1Data.total_unboxed_value),
        total_products: formatProducts(player1Products),
      })
    );

    dispatch(
      updatePlayer2({
        winner: Boolean(player2Data.is_winner),
        total_unboxed_value: parseFloat(player2Data.total_unboxed_value),
        total_amount: parseFloat(player2Data.total_unboxed_value),
        total_products: formatProducts(player2Products),
      })
    );

    if (isCoinFlipping) {
      await replayDelay(5000);
    }

    dispatch(updateBattle({ flip: false }));
    setIsCoinFlipping(false);
  };

  const processReplaySequence = async (apiResponse) => {
    try {
      dispatch(updateBattle({ status: "running" }));
      setBattleStarted(true);
      const iterationsByBox = {};

      apiResponse.iterations.forEach((iteration) => {
        const boxIndex = iteration.box_index;
        if (!iterationsByBox[boxIndex]) {
          iterationsByBox[boxIndex] = {
            box_index: boxIndex,
            selectedProducts: [],
          };
        }
        iterationsByBox[boxIndex].selectedProducts.push(
          ...iteration.selectedProducts
        );
      });

      const sortedBoxIndices = Object.keys(iterationsByBox)
        .map((k) => parseInt(k))
        .sort((a, b) => a - b);

      for (const boxIndex of sortedBoxIndices) {
        if (replayCancelledRef.current) {
          throw new Error("REPLAY_CANCELLED");
        }
        const groupedIteration = iterationsByBox[boxIndex];
        await handleReplayTick(
          groupedIteration,
          boxIndex,
          apiResponse
        );
      }

      if (replayCancelledRef.current) {
        throw new Error("REPLAY_CANCELLED");
      }

      if (apiResponse.flip === true) {
        await handleReplayFlip(apiResponse);
      }

      if (replayCancelledRef.current) {
        throw new Error("REPLAY_CANCELLED");
      }

      await handleReplayEnd(apiResponse);

      setIsReplaying(false);
    } catch (error) {
      if (error.message === "REPLAY_CANCELLED") {
        restoreFinishedState(apiResponse);
        return;
      }
      console.error("❌ Replay sequence error:", error);
      setIsReplaying(false);
      throw error;
    }
  };

  const restoreFinishedState = (apiResponse) => {
    const player1Data = apiResponse.players[0];
    const player2Data = apiResponse.players[1];

    const player1Products =
      apiResponse.wonProducts?.filter(
        (p) => p.player_id === player1Data.player_id
      ) || [];

    const player2Products =
      apiResponse.wonProducts?.filter(
        (p) => p.player_id === player2Data.player_id
      ) || [];

    const formatProducts = (products) =>
      products.map((item) => ({
        image: item.product_image,
        name: item.product_name,
        price: item.product_value,
      }));

    const winnerId = player1Data.is_winner
      ? player1Data.user_id
      : player2Data.is_winner
      ? player2Data.user_id
      : null;

    if (winnerId) {
      setFlipWinner(winnerId);
    }

    dispatch(
      updateBattle({
        status: "finished",
        flip: false,
        finished_at:
          apiResponse.battle?.finished_at || new Date().toISOString(),
        box_detail: {
          ...battle?.box_detail,
          current_box:
            battle?.box_count || battle?.box_detail?.total_box || 1,
          animating_box: 0,
        },
      })
    );

    dispatch(
      updatePlayer1({
        winner: Boolean(player1Data.is_winner),
        total_unboxed_value: parseFloat(player1Data.total_unboxed_value),
        total_amount: parseFloat(player1Data.total_unboxed_value),
        total_products: formatProducts(player1Products),
        user_product: undefined,
      })
    );

    dispatch(
      updatePlayer2({
        winner: Boolean(player2Data.is_winner),
        total_unboxed_value: parseFloat(player2Data.total_unboxed_value),
        total_amount: parseFloat(player2Data.total_unboxed_value),
        total_products: formatProducts(player2Products),
        user_product: undefined,
      })
    );

    setBattleStarted(true);
    setIsCoinFlipping(false);
    setCoinAnimationComplete(false);
    setIsReplaying(false);
  };

  // Detect JS execution pauses during replay (DevTools, tab backgrounding)
  useEffect(() => {
    if (!isReplaying) return;

    let lastCheckTime = Date.now();
    const THRESHOLD_MS = 2000;

    const watchdogInterval = setInterval(() => {
      const now = Date.now();
      const elapsed = now - lastCheckTime;
      if (elapsed > 500 + THRESHOLD_MS) {
        replayCancelledRef.current = true;
        clearInterval(watchdogInterval);
      }
      lastCheckTime = now;
    }, 500);

    return () => {
      clearInterval(watchdogInterval);
    };
  }, [isReplaying]);

 const handleBattleReplay = async () => {
  if (isReplaying) return;

  replayCancelledRef.current = false;

  try {
    const apiResponse = await fetchBattleResult(
      JSON.stringify(decryptedBattleId)
    );

    if (!apiResponse || !apiResponse.iterations || !apiResponse.players) {
      alert("Failed to load replay data");
      return;
    }
    if (apiResponse.players.length < 2) {
      alert("Incomplete battle data");
      return;
    }

    replayFinishedStateRef.current = apiResponse;

    if (battle?.uuid) {
      battleWSManager.unsubscribe(battle.uuid);
    }

    // ✅ FIX: Set battleStarted to false BEFORE setting isReplaying to true.
    // This prevents the brief frame where both are true with stale data.
    setBattleStarted(false);
    setIsCoinFlipping(false);
    setCoinAnimationComplete(false);
    setFlipWinner(null);

    // ✅ FIX: Now set isReplaying — shouldShowBattleRoulete stays false
    // because battleStarted is already false.
    setIsReplaying(true);

    dispatch(
      updatePlayer1({
        total_unboxed_value: 0,
        total_amount: 0,
        total_products: [],
        winner: false,
        user_product: undefined,
      })
    );

    dispatch(
      updatePlayer2({
        total_unboxed_value: 0,
        total_amount: 0,
        total_products: [],
        winner: false,
        user_product: undefined,
      })
    );

    dispatch(
      updateBattle({
        status: "replay",
        flip: false,
        box_products: [],
        all_box_products: [],
        box_detail: {
          ...battle?.box_detail,
          current_box: 1,
          animating_box: 0,
        },
      })
    );

    lastProcessedBoxIndexRef.current = -1;

    await delay(500);

    if (replayCancelledRef.current) {
      restoreFinishedState(apiResponse);
      return;
    }

    await processReplaySequence(apiResponse);

    if (!replayCancelledRef.current) {
      setTimeout(() => {
        if (battle?.uuid) battleWSManager.subscribe(battle.uuid);
      }, 3000);
    } else {
      if (battle?.uuid) battleWSManager.subscribe(battle.uuid);
    }
  } catch (error) {
    if (error.message === "REPLAY_CANCELLED") {
      if (battle?.uuid) battleWSManager.subscribe(battle.uuid);
      return;
    }
    console.error("❌ Replay error:", error);
    alert("Failed to replay battle. Please try again.");
    setIsReplaying(false);
    if (replayFinishedStateRef.current) {
      restoreFinishedState(replayFinishedStateRef.current);
    }
    if (battle?.uuid) battleWSManager.subscribe(battle.uuid);
  }
};

  // ✅ FIX: Process event queue with robust duplicate and skip handling
  useEffect(() => {
    if (!isInitialized) {
      return;
    }

    if (eventQueue.length === 0 || processingRef.current || isReplaying) {
      return;
    }

    const processNextEvent = async () => {
      processingRef.current = true;
      dispatch(setProcessing(true));

      const firstEvent = eventQueue[0];

      const eventChannel = firstEvent._channel;
      const expectedChannel = `battle.${battle?.uuid}`;

      if (eventChannel !== expectedChannel && battle?.uuid) {
        console.warn("⚠️ Skipping event from different battle:", {
          eventChannel,
          expectedChannel,
          currentBattleUuid: battle?.uuid,
          eventType: firstEvent.type,
        });
        dispatch(removeFirstEvent());
        processingRef.current = false;
        dispatch(setProcessing(false));
        return;
      }

      // ✅ FIX: Skip duplicate tick events that have already been processed.
      // This handles the case where a duplicate tick remains in the queue
      // (e.g., the skip logic jumped to tick(3) but a second tick(3) exists).
      if (
        firstEvent.type === "tick" &&
        firstEvent.box_index <= lastProcessedBoxIndexRef.current
      ) {
        console.warn(
          `⚠️ Removing already-processed tick event for box_index ${firstEvent.box_index} from queue`
        );
        dispatch(removeFirstEvent());
        processingRef.current = false;
        dispatch(setProcessing(false));
        return;
      }

      if (firstEvent.type === "tick" && battleStarted) {
        const latestTick = skipToLatestTick(eventQueue);

        if (latestTick && latestTick.box_index > firstEvent.box_index) {
          // ✅ FIX: Remove ALL tick events up to and including the latest,
          // EXCEPT the latest itself which we process. But also remove
          // any duplicates of the latest tick.
          // Count how many events to remove from the front before the
          // latest tick (we need to be precise with positional removal).
          let eventsToRemoveCount = 0;
          for (let i = 0; i < eventQueue.length; i++) {
            if (
              eventQueue[i].type === "tick" &&
              eventQueue[i].box_index === latestTick.box_index &&
              eventQueue[i]._id === latestTick._id
            ) {
              // Found the latest tick we want to process — stop counting
              break;
            }
            eventsToRemoveCount++;
          }

          // Remove everything before the latest tick
          for (let i = 0; i < eventsToRemoveCount; i++) {
            dispatch(removeFirstEvent());
          }

          try {
            await handleTickEvent(latestTick);
          } catch (error) {
            console.error(
              `Error processing skipped tick ${latestTick.box_index}:`,
              error
            );
          }

          // Remove the latest tick we just processed
          dispatch(removeFirstEvent());

          // ✅ FIX: Also remove any remaining duplicate ticks for the
          // same box_index that might still be in the queue
          // (We check the live Redux state via a thunk)
          dispatch((dispatch, getState) => {
            let queue = getState().battle.eventQueue;
            while (
              queue.length > 0 &&
              queue[0].type === "tick" &&
              queue[0].box_index <= lastProcessedBoxIndexRef.current
            ) {
              dispatch(removeFirstEvent());
              queue = getState().battle.eventQueue;
            }
          });

          processingRef.current = false;
          dispatch(setProcessing(false));
          return;
        }
      }

      const event = eventQueue[0];

      try {
        switch (event.type) {
          case "second_player_joined":
            await handleSecondPlayerJoined(event);
            break;

          case "started":
            await handleBattleStarted(event);
            break;

          case "tick":
            await handleTickEvent(event);
            break;

          case "flip":
            await handleFlipEvent(event);
            break;

          case "ended":
            await handleBattleEnded(event);
            break;

          default:
        }
      } catch (error) {
        console.error(`Error processing event ${event.type}:`, error);
      }

      dispatch(removeFirstEvent());
      processingRef.current = false;
      dispatch(setProcessing(false));
    };

    processNextEvent();
  }, [
    eventQueue,
    battle?.uuid,
    battle?.creator_id,
    isReplaying,
    battleStarted,
    isInitialized,
    skipToLatestTick,
    handleSecondPlayerJoined,
    handleBattleStarted,
    handleTickEvent,
    handleFlipEvent,
    handleBattleEnded,
    dispatch,
  ]);

  // ==================== INITIAL API CALL ====================
  useEffect(() => {
    if (!decryptedBattleId) {
      dispatch(toggleLoader({ isLoading: false }));
      return;
    }

    const api = async () => {
      dispatch(toggleLoader({ isLoading: true }));

      try {
        const response = await fetchBattleDetails(
          JSON.stringify(decryptedBattleId)
        );

        if (response?.success === false || !response?.battle) {
          dispatch(setBattle(null));
        } else {
          const battleData = response?.battle;

          if (response?.share_link) {
            setShareLink(response.share_link);
          }

          const player1Data = battleData?.players?.[0];
          const player2Data = battleData?.players?.[1];

          const formatProductsFromAPI = (products) => {
            if (!products || !Array.isArray(products)) return [];
            return products.map((item) => ({
              image: item.image,
              name: item.name,
              price: parseFloat(item.price || 0),
            }));
          };

          let player1WonProducts = [];
          let player2WonProducts = [];

          const apiCurrentTick =
            response?.current_tick || battleData?.current_tick || 0;
          const isRunningBattle =
            battleData?.status === "started" ||
            battleData?.status === "running";
          const apiIsLateJoin = isRunningBattle && apiCurrentTick > 0;

          if (battleData?.status === "finished") {
            if (battleData?.player_1_products) {
              player1WonProducts = formatProductsFromAPI(
                battleData.player_1_products
              );
            }

            if (battleData?.player_2_products) {
              player2WonProducts = formatProductsFromAPI(
                battleData.player_2_products
              );
            }

            if (
              player1WonProducts.length === 0 &&
              player2WonProducts.length === 0 &&
              battleData?.won_products
            ) {
              player1WonProducts = formatProductsFromAPI(
                battleData.won_products.filter(
                  (p) =>
                    p.player_id === player1Data?.id ||
                    p.user_id === player1Data?.user_id
                )
              );

              player2WonProducts = formatProductsFromAPI(
                battleData.won_products.filter(
                  (p) =>
                    p.player_id === player2Data?.id ||
                    p.user_id === player2Data?.user_id
                )
              );
            }
          } else if (apiIsLateJoin && response?.wonProducts) {
            player1WonProducts = formatWonProductsFromAPI(
              response.wonProducts,
              player1Data?.id,
              battleData?.creator_id,
              apiCurrentTick
            );

            player2WonProducts = formatWonProductsFromAPI(
              response.wonProducts,
              player2Data?.id,
              battleData?.creator_id,
              apiCurrentTick
            );
          }

          const transformedBattle = {
            ...battleData,
            id: battleData?.id,
            uuid: battleData?.uuid,
            creator_id: battleData?.creator_id,
            mode: battleData?.mode,
            stake_amount: battleData?.stake_amount,
            box_count: battleData?.box_count,
            boxes: battleData?.boxes,
            creator: battleData?.creator,
            players: battleData?.players,
            status:
              battleData?.status === "started"
                ? "running"
                : battleData?.status,
            ready_to_start: battleData?.ready_to_start,
            started_at: battleData?.started_at,
            finished_at: battleData?.finished_at,
            metadata: battleData?.metadata,
            share_link: response?.share_link,
            flip: false,

            user_1: player1Data
              ? {
                  id: player1Data.user_id,
                  user_id: player1Data.user_id,
                  player_id: player1Data.id,
                  user: player1Data.user,
                  userdetails: player1Data.user,
                  winner: Boolean(player1Data.is_winner),
                  total_products: player1WonProducts,
                  total_unboxed_value: parseFloat(
                    player1Data.total_unboxed_value || 0
                  ),
                  total_amount: parseFloat(
                    player1Data.total_unboxed_value || 0
                  ),
                }
              : {
                  id: battleData?.creator_id,
                  user_id: battleData?.creator_id,
                  user: battleData?.creator,
                  userdetails: battleData?.creator,
                  winner: false,
                  total_products: [],
                  total_unboxed_value: 0,
                  total_amount: 0,
                },

            user_2: player2Data
              ? {
                  id: player2Data.user_id,
                  user_id: player2Data.user_id,
                  player_id: player2Data.id,
                  user: player2Data.user,
                  userdetails: player2Data.user,
                  winner: Boolean(player2Data.is_winner),
                  total_products: player2WonProducts,
                  total_unboxed_value: parseFloat(
                    player2Data.total_unboxed_value || 0
                  ),
                  total_amount: parseFloat(
                    player2Data.total_unboxed_value || 0
                  ),
                }
              : null,

            box_detail: {
              current_box:
                battleData?.status === "finished"
                  ? battleData?.box_count
                  : apiCurrentTick > 0
                  ? apiCurrentTick + 1
                  : 1,
              total_box: battleData?.box_count,
              battle_price: battleData?.stake_amount,
              boxes: battleData?.boxes,
              box: battleData?.boxes?.[0]?.box,
            },
          };

          dispatch(setBattle(transformedBattle));

          const hasPlayer2 = battleData?.players?.length >= 2;
          if (hasPlayer2) {
            dispatch(setPlayer2Joined(true));
          }

          // Set initial state based on battle status
          if (battleData?.status === "waiting") {
            setIsWaitingOverride(true);
          } else if (battleData?.status === "starting") {
            const countdownAlreadyShown = sessionStorage.getItem(
              `countdownShown-${decryptedBattleId}`
            );
            if (countdownAlreadyShown !== "true") {
              setShowStartCountdown(true);
              startedEventReceivedRef.current = true;
            }
          } else if (isRunningBattle) {
            if (apiIsLateJoin) {
              setBattleStarted(true);
              setShowStartCountdown(false);
              setIsLateJoin(true);
              isLateJoinRef.current = true;
              setIsWaitingOverride(false);
              sessionStorage.setItem(
                `battleStarted-${battleData?.id}`,
                "true"
              );
              sessionStorage.setItem(
                `countdownShown-${decryptedBattleId}`,
                "true"
              );
              countdownShownRef.current = true;
              startedEventReceivedRef.current = true;

              // ✅ FIX: Seed lastProcessedBoxIndexRef from API's current_tick.
              // If the API says the battle is on tick 3, boxes 0-2 are done.
              // Set the ref to (apiCurrentTick - 1) so that if a duplicate
              // tick for any already-completed box arrives, it gets rejected.
              if (apiCurrentTick > 0) {
                lastProcessedBoxIndexRef.current = apiCurrentTick - 2;
              }
            } else {
              const startedAt = new Date(battleData?.started_at);
              const now = new Date();
              const secondsSinceStart = (now - startedAt) / 1000;

              if (secondsSinceStart > 10) {
                setBattleStarted(true);
                setShowStartCountdown(false);
                setIsLateJoin(true);
                isLateJoinRef.current = true;
                setIsWaitingOverride(false);
                sessionStorage.setItem(
                  `battleStarted-${battleData?.id}`,
                  "true"
                );
                sessionStorage.setItem(
                  `countdownShown-${decryptedBattleId}`,
                  "true"
                );
                countdownShownRef.current = true;
                startedEventReceivedRef.current = true;
              }
            }
          } else if (battleData?.status === "finished") {
            setShowStartCountdown(false);
            setBattleStarted(true);
            setIsWaitingOverride(false);
            countdownShownRef.current = true;
            dispatch(clearEventQueue());

            // ✅ FIX: Mark all boxes as processed for finished battles
            lastProcessedBoxIndexRef.current =
              (battleData?.box_count || 1) - 1;

            const winnerId = player1Data?.is_winner
              ? player1Data.user_id
              : player2Data?.is_winner
              ? player2Data.user_id
              : null;

            if (winnerId) {
              setFlipWinner(winnerId);
            }
          }

          setIsInitialized(true);
        }
      } catch (error) {
        console.error("❌ API Error:", error);
        dispatch(setBattle(null));
      } finally {
        dispatch(toggleLoader({ isLoading: false }));
      }
    };

    api();
  }, [decryptedBattleId, dispatch, formatWonProductsFromAPI]);

  // WebSocket subscription
  useEffect(() => {
    if (!battle?.uuid || isReplaying || !isInitialized) {
      return;
    }

    battleWSManager.subscribe(battle.uuid);

    prevBattleUuidRef.current = battle.uuid;

    return () => {
      battleWSManager.forceUnsubscribe(battle.uuid);
    };
  }, [battle?.uuid, isReplaying, isInitialized]);

  // Enter battle check
  useEffect(() => {
    if (!battle?.id) return;
    const hasEntered = localStorage.getItem(`battle_entered_${battle.id}`);
    if (hasEntered === "true") {
      setHasEnteredBattle(true);
    }
  }, [battle?.id]);

  useEffect(() => {
    const handleEnterBattle = async () => {
      if (
        battle?.status === "running" &&
        !hasEnteredBattle &&
        user?.id === battle?.creator_id &&
        isInitialized
      ) {
        try {
          await enterBattle(battle?.id);
          localStorage.setItem(`battle_entered_${battle?.id}`, "true");
          setHasEnteredBattle(true);
        } catch (error) {
          console.error("❌ Enter battle failed:", error);
        }
      }
    };

    handleEnterBattle();
  }, [
    battle?.status,
    battle?.id,
    user?.id,
    battle?.creator_id,
    hasEnteredBattle,
    isInitialized,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (prevBattleUuidRef.current) {
        battleWSManager.forceUnsubscribe(prevBattleUuidRef.current);
      }
      replayCancelledRef.current = true;
      dispatch(clearBattle());
      dispatch(clearEventQueue());
    };
  }, [dispatch]);

  // Helper function
  const isUserInBattle = useCallback(() => {
    if (!battle) return false;
    if (user?.id === battle?.creator_id) return true;
    const isPlayer = battle?.players?.some(
      (player) => player?.user_id === user?.id
    );
    return isPlayer;
  }, [battle, user?.id]);

  // Compute render conditions
  const isBattleWaiting =
    isInitialized &&
    (battle?.status === "waiting" || isWaitingOverride) &&
    !battleStarted &&
    !isReplaying;

  const shouldShowStartCountdown =
    isInitialized &&
    showStartCountdown &&
    !countdownShownRef.current &&
    !isLateJoin;

  const shouldShowBattleEnd = useMemo(() => {
    const hasWinner = battle?.user_1?.winner || battle?.user_2?.winner;

    if (isReplaying) {
      return false;
    }

    if (battle?.status === "replay") {
      return false;
    }

    return hasWinner;
  }, [
    battle?.user_1?.winner,
    battle?.user_2?.winner,
    battle?.status,
    isReplaying,
  ]);

  const shouldShowBattleRoulete = useMemo(() => {
    if (isReplaying && battleStarted) {
      return true;
    }

    if (battle?.status === "replay" && battleStarted) {
      return true;
    }

    return (
      (battle?.status === "running" || battle?.status === "starting") &&
      battleStarted &&
      !shouldShowBattleEnd
    );
  }, [battle?.status, battleStarted, isReplaying, shouldShowBattleEnd]);

  // ==================== RENDER ====================

  return (
    <>
      {isLoading ? (
        <FullAppLoader />
      ) : battle == null ? (
        <PageNotFound />
      ) : !isInitialized ? (
        <FullAppLoader />
      ) : !showOtherComponent && countDown ? (
        <Countdown onComplete={handleCountdownComplete} height="100vh" />
      ) : shouldShowStartCountdown &&
        battle?.status !== "finished" &&
        !isLateJoin ? (
        <Countdown
          onComplete={handleStartCountdownComplete}
          height="100vh"
        />
      ) : isBattleWaiting && !isUserInBattle() ? (
        <>
          <BattleHeader battleDetail={battle} />
          <BattleWait
            battle={battle}
            shareLink={shareLink}
            player2Joined={player2Joined}
          />
        </>
      ) : isUserInBattle() || battle?.status !== "waiting" ? (
        <>
          {battle?.flip ? (
            <CoinFlip
              player1={battle?.user_1}
              player2={battle?.user_2}
              battle_id={JSON.stringify(decryptedBattleId)}
              setBattle={(updates) => dispatch(updateBattle(updates))}
              flipWinner={flipWinner}
            />
          ) : (
            <>
              {!battle?.exception && battle ? (
                <BattleHeader battleDetail={battle} />
              ) : null}

              {shouldShowBattleEnd ? (
                <>
                  <BattleEnd
                    player1={battle?.user_1}
                    player2={battle?.user_2}
                    onReplay={handleBattleReplay}
                    isReplaying={isReplaying}
                  />
                </>
              ) : shouldShowBattleRoulete ? (
                <BattleRoulete
                  products={battle?.all_box_products || []}
                  player1={battle?.user_1}
                  player2={battle?.user_2}
                  boxCount={battle?.box_detail?.current_box}
                  rollSound={rollSound}
                  winSound={winSound}
                  battleDetail={battle}
                />
              ) : !isReplaying ? (
                <BattleWait
                  battle={battle}
                  shareLink={shareLink}
                  player2Joined={player2Joined}
                />
              ) : null}

              {battle?.user_1?.user?.id && battle?.user_2?.user?.id ? (
                <BattleFooter
                  player1={battle?.user_1}
                  player2={battle?.user_2}
                  totalBoxes={
                    battle?.box_count || battle?.box_detail?.total_box
                  }
                  currentBox={battle?.box_detail?.current_box}
                />
              ) : null}
            </>
          )}
        </>
      ) : (
        <PageNotFound />
      )}
    </>
  );
};

export default BattleDetail;